import { useEffect } from "react";
import Loading from "../../components/Loading";
import { arrayMap, findItemByFiled } from "../../utils/array.utils";
import { useGetAllCarInfoQuery, useGetGroupCarInfoQuery } from "../../utils/carInfo.utils";
import { useGetAllCarInfoTypeQuery } from "../../utils/carInfoType.utils";
import { RenderFields, optionGen } from "../../utils/form.utils";
import { textCamelCase } from "../../utils/string.utils";

export default function BrandField({ watch, control, errors, setValue }) {
  const brandId = watch("brand")?.value;
  const modelId = watch("model")?.value;
  const chassis = watch("chassis");
  const mileage = watch("mileage");
  const askingPrice = watch("asking_price");
  const discountPrice = watch("discount_price");

  useEffect(() => {
    if (Number(chassis) < 0) {
      setValue("chassis", 0);
    }

    if (Number(mileage) < 0) {
      setValue("mileage", 0);
    }
    if (Number(askingPrice) < 0) {
      setValue("asking_price", 0);
    }

    if (Number(discountPrice) < 0) {
      setValue("discount_price", 0);
    }
  }, [chassis, mileage, askingPrice, discountPrice, setValue]);

  const { loading: parentLoading, data: parentData } = useGetGroupCarInfoQuery({
    query: ``,
    key: `GetParentGroupCarInfoForStockListAdd`
  });

  const { isLoading: childLoading, data: childData } = useGetGroupCarInfoQuery({
    query: `?in=0&parent=${modelId}`,
    key: `GetChildGroupCarInfoForStockListAdd`,
    options: {
      enabled: !!modelId
    }
  });

  const { loading: typeLoading, data: typeData } = useGetAllCarInfoTypeQuery({
    query: ``,
    key: `GetTypeDataForStockListAdd`
  });

  function handleTypeId(slug) {
    return typeData?.length ? findItemByFiled(typeData, "slug", slug)?.id : null;
  }

  let typeIds = {};

  const typeSlugs = arrayMap(typeData, (item) => item?.slug) || [];

  typeSlugs.forEach((element) => {
    typeIds[textCamelCase(element)] = handleTypeId(element);
  });

  const modelTypeId = typeIds?.model;

  const { isLoading: modelLoading, data: modelData } = useGetAllCarInfoQuery({
    query: `?parent=${brandId}&type=${modelTypeId}`,
    options: {
      enabled: !!brandId
    },
    key: "GetModelDataForStockListAdd"
  });

  function handleParentData(name) {
    return parentData[name] ? optionGen(parentData[name]) : [];
  }

  function handleChildData(name) {
    return childData[name] ? optionGen(childData[name]) : [];
  }

  const brandOptions = handleParentData("brand");
  const colorOptions = handleParentData("color");
  const ccOptions = handleParentData("cubic-capacity-(cc)");
  const gradeOptions = handleParentData("auction-grade");
  const fuelOptions = handleParentData("fuel");
  const yearOptions = handleParentData("year");
  const bodyTypeOptions = handleParentData("body-type");

  const modelOptions = modelData?.length ? optionGen(modelData) : [];

  const packageOptions = handleChildData("package");
  const chassisOptions = handleChildData("chassis-code");
  const featureOptions = handleChildData("features");

  const fields = [
    {
      label: "Brand",
      name: "brand",
      type: "select",
      placeholder: "Select a brand",
      col: { md: 4 },
      options: brandOptions,
      // isDisabled: !brandTypeId,
      rules: { required: "This is required field!" }
    },
    {
      label: "Model",
      name: "model",
      type: "select",
      placeholder: "Select a model",
      col: { md: 4 },
      options: modelOptions,
      // isDisabled: !brandId,
      rules: { required: "This is required field!" }
    },
    {
      label: "Package",
      name: "package",
      type: "select",
      placeholder: "Select a package",
      col: { md: 4 },
      options: packageOptions,
      // isDisabled: !modelId,
      rules: { required: "This is required field!" }
    },
    {
      label: "Color",
      name: "color",
      type: "select",
      placeholder: "Select a color",
      col: { md: 4 },
      options: colorOptions,
      // isDisabled: colorLoading,
      rules: { required: "This is required field!" }
    },
    {
      label: "Cubic Capacity (CC)",
      name: "cc",
      type: "select",
      placeholder: "Select a cc",
      col: { md: 4 },
      options: ccOptions,
      // isDisabled: ccLoading,
      rules: { required: "This is required field!" }
    },
    {
      label: "Auction Grade",
      name: "grade",
      type: "select",
      placeholder: "Select a grade",
      col: { md: 4 },
      options: gradeOptions,
      // isDisabled: gradeLoading,
      rules: { required: "This is required field!" }
    },
    {
      label: "Fuel",
      name: "fuel",
      type: "select",
      placeholder: "Select a fuel",
      col: { md: 4 },
      options: fuelOptions,
      // isDisabled: fuelLoading,
      rules: { required: "This is required field!" }
    },
    {
      label: "Chassis Code",
      name: "chassis_code",
      type: "select",
      placeholder: "Select a chassis code",
      col: { md: 4 },
      options: chassisOptions,
      // isDisabled: !modelId,
      rules: { required: "This is required field!" }
    },
    {
      label: "Chassis Number",
      name: "chassis",
      type: "number",
      placeholder: "Enter a chassis number",
      col: { md: 4 },
      rules: { required: "This is required field!" }
    },
    {
      label: "Mileage",
      name: "mileage",
      type: "number",
      placeholder: "Enter mileage",
      col: { md: 4 },
      rules: { required: "This is required field!" }
    },
    {
      label: "Year",
      name: "year",
      type: "select",
      placeholder: "Select a year",
      col: { md: 4 },
      options: yearOptions,
      // isDisabled: yearLoading,
      rules: { required: "This is required field!" }
    },
    {
      label: "Features",
      name: "features",
      type: "select",
      placeholder: "Select features",
      col: { md: 4 },
      options: featureOptions,
      // isDisabled: featureLoading,
      isMulti: true
    },
    {
      label: "Body Type",
      name: "body_type",
      type: "select",
      placeholder: "Select a body type",
      col: { md: 4 },
      options: bodyTypeOptions
    },
    {
      label: "Asking Price",
      name: "asking_price",
      type: "number",
      placeholder: "Enter a asking price",
      col: { md: 4 }
    },
    {
      label: "Discount Price",
      name: "discount_price",
      type: "number",
      placeholder: "Enter a discount price",
      col: { md: 4 }
    }
  ];

  const isLoading = typeLoading || modelLoading || parentLoading || childLoading;

  return (
    <>
      <Loading loading={isLoading} />
      {
        <RenderFields
          setValue={setValue}
          fields={fields}
          control={control}
          watch={watch}
          errors={errors}
        />
      }
    </>
  );
}
